import React, { useEffect, useRef, useState } from 'react';
import {
  App,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { TableColumnsType } from 'antd';
import { AppstoreAddOutlined, CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../utils/feedback';
import {
  DIMENSION_DISPLAY_PROFILES,
  formatCatalogProductDimensions,
} from '../../utils/productDimensions';
import {
  ProductCategory,
  ProductCategoryAssignedProduct,
  categoryErrorMessage,
  createProductCategoryTag,
  deleteProductCategoryTag,
  fetchProductCategoryAssignmentCandidates,
  fetchProductCategoryAssignments,
  fetchProductCategoryTags,
  replaceProductCategoryAssignments,
  updateProductCategoryTag
} from '../../modules/products/productCategoriesApi';

const { Text } = Typography;
const { TextArea } = Input;

// Preset swatches so the saved value is always a valid #rrggbb hex (matches the
// backend COLOR_PATTERN). "none" clears the color.
const COLOR_PRESETS = ['#1677ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#8c8c8c'];
const PRODUCT_PREVIEW_NAME_MAX_LENGTH = 36;

interface ProductCategoriesTabProps {
  canEdit: boolean;
  /** Called after any create/update/delete so the parent can refresh selects/filters. */
  onCategoriesChanged?: () => void;
}

interface CategoryFormValues {
  name: string;
  description?: string | null;
  color?: string | null;
  dimensionDisplayProfile?: string | null;
}

type AssignmentProductOption = ProductCategoryAssignedProduct & {
  productCategoryName?: string | null;
};

const buildProductAssignmentSearchLabel = (product: ProductCategoryAssignedProduct) => [
  product.code,
  product.name,
  product.englishName,
  product.ean,
  formatCatalogProductDimensions(product)
].filter(Boolean).join(' ');

function getProductPreviewName(product: ProductCategoryAssignedProduct): string {
  const name = product.name.trim();
  if (name.length <= PRODUCT_PREVIEW_NAME_MAX_LENGTH) return name;
  return `${name.slice(0, PRODUCT_PREVIEW_NAME_MAX_LENGTH - 1).trimEnd()}…`;
}

const ProductCategoriesTab: React.FC<ProductCategoriesTabProps> = ({ canEdit, onCategoriesChanged }) => {
  const { t } = useTranslation(['products', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [products, setProducts] = useState<ProductCategoryAssignedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductCategory | null>(null);
  const [assigningCategory, setAssigningCategory] = useState<ProductCategory | null>(null);
  const [assignmentProductOptions, setAssignmentProductOptions] = useState<AssignmentProductOption[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentReady, setAssignmentReady] = useState(false);
  const [assignmentSelectOpen, setAssignmentSelectOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const assignmentSessionRef = useRef(0);
  const [form] = Form.useForm<CategoryFormValues>();

  const loadCategories = async () => {
    setLoading(true);
    try {
      setCategories(await fetchProductCategoryTags());
    } catch (error) {
      feedback.error(t('products:productCategory.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      setProducts(await fetchProductCategoryAssignmentCandidates());
    } catch (error) {
      feedback.error(t('products:productCategory.productsLoadFailed'));
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ dimensionDisplayProfile: 'GENERIC' });
    setModalOpen(true);
  };

  const openEdit = (category: ProductCategory) => {
    setEditing(category);
    form.setFieldsValue({
      name: category.name,
      description: category.description ?? undefined,
      color: category.color ?? undefined,
      dimensionDisplayProfile: category.dimensionDisplayProfile || 'GENERIC'
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const mergeAssignmentOptions = (
    allProducts: ProductCategoryAssignedProduct[],
    assignedProducts: ProductCategoryAssignedProduct[],
    category: ProductCategory
  ) => {
    const byId = new Map<string, AssignmentProductOption>();
    allProducts.forEach((product) => byId.set(product.id, product));
    assignedProducts.forEach((product) => {
      byId.set(product.id, {
        ...byId.get(product.id),
        ...product,
        productCategoryName: byId.get(product.id)?.productCategoryName ?? category.name
      });
    });
    return Array.from(byId.values()).sort((a, b) => {
      const left = `${a.code || ''} ${a.name}`.trim();
      const right = `${b.code || ''} ${b.name}`.trim();
      return left.localeCompare(right, 'pl');
    });
  };

  const openAssignments = async (category: ProductCategory) => {
    const sessionId = assignmentSessionRef.current + 1;
    assignmentSessionRef.current = sessionId;
    setAssigningCategory(category);
    setSelectedProductIds([]);
    setAssignmentProductOptions([]);
    setAssignmentReady(false);
    setAssignmentSelectOpen(false);
    setAssignmentModalOpen(true);
    setAssignmentLoading(true);
    try {
      const [freshProducts, assignedProducts] = await Promise.all([
        fetchProductCategoryAssignmentCandidates(),
        fetchProductCategoryAssignments(category.id)
      ]);
      if (assignmentSessionRef.current !== sessionId) return;
      setProducts(freshProducts);
      setAssignmentProductOptions(mergeAssignmentOptions(freshProducts, assignedProducts, category));
      setSelectedProductIds(assignedProducts.map((product) => product.id));
      setAssignmentReady(true);
    } catch (error) {
      if (assignmentSessionRef.current !== sessionId) return;
      feedback.error(t('products:productCategory.assignmentLoadFailed'));
    } finally {
      if (assignmentSessionRef.current === sessionId) {
        setAssignmentLoading(false);
      }
    }
  };

  const closeAssignmentModal = () => {
    if (savingAssignments) return;
    assignmentSessionRef.current += 1;
    setAssignmentModalOpen(false);
    setAssigningCategory(null);
    setAssignmentProductOptions([]);
    setSelectedProductIds([]);
    setAssignmentReady(false);
    setAssignmentSelectOpen(false);
  };

  const handleSubmit = async (values: CategoryFormValues) => {
    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        color: values.color || null,
        dimensionDisplayProfile: values.dimensionDisplayProfile || 'GENERIC'
      };
      if (editing) {
        await updateProductCategoryTag(editing.id, payload);
        feedback.success(t('products:productCategory.updatedSuccess', { name: payload.name }));
      } else {
        await createProductCategoryTag(payload);
        feedback.success(t('products:productCategory.createdSuccess', { name: payload.name }));
      }
      setModalOpen(false);
      setEditing(null);
      await loadCategories();
      await loadProducts();
      onCategoriesChanged?.();
    } catch (error: any) {
      feedback.error(categoryErrorMessage(error, t, t('products:productCategory.saveFailed', { name: values.name.trim() })));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!assigningCategory) return;
    if (!assignmentReady) {
      feedback.error(t('products:productCategory.assignmentLoadFailed'));
      return;
    }

    setSavingAssignments(true);
    try {
      await replaceProductCategoryAssignments(assigningCategory.id, selectedProductIds);
      feedback.success(t('products:productCategory.assignmentsSaved', { name: assigningCategory.name }));
      setAssignmentModalOpen(false);
      setAssigningCategory(null);
      setSelectedProductIds([]);
      await Promise.all([loadCategories(), loadProducts()]);
      onCategoriesChanged?.();
    } catch (error: any) {
      feedback.error(categoryErrorMessage(error, t, t('products:productCategory.assignFailed', { name: assigningCategory.name })));
    } finally {
      setSavingAssignments(false);
    }
  };

  const handleDelete = (category: ProductCategory) => {
    const productCount = category.productCount ?? 0;
    modal.confirm({
      title: t('products:productCategory.deleteConfirmTitle'),
      content: productCount > 0
        ? t('products:productCategory.deleteConfirmWithProducts', { count: productCount })
        : t('products:productCategory.deleteConfirm'),
      okText: t('common:yes'),
      cancelText: t('common:no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingId(category.id);
        try {
          await deleteProductCategoryTag(category.id);
          feedback.success(t('products:productCategory.deletedSuccess', { name: category.name }));
          await Promise.all([loadCategories(), loadProducts()]);
          onCategoriesChanged?.();
        } catch (error: any) {
          feedback.error(categoryErrorMessage(error, t, t('products:productCategory.deleteFailed', { name: category.name })));
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const getCategoryProducts = (categoryId: string) =>
    products.filter((product) => product.productCategoryId === categoryId);

  const renderProductPreview = (categoryId: string) => {
    const assignedProducts = getCategoryProducts(categoryId);
    if (assignedProducts.length === 0) {
      return <Text type="secondary">{t('products:productCategory.noAssignedProducts')}</Text>;
    }

    const visibleProducts = assignedProducts.slice(0, 3);
    return (
      <Space size={[4, 4]} wrap>
        {visibleProducts.map((product) => (
          <Tooltip key={product.id} title={product.name}>
            <Tag>{getProductPreviewName(product)}</Tag>
          </Tooltip>
        ))}
        {assignedProducts.length > visibleProducts.length && (
          <Text type="secondary">
            {t('products:productCategory.productsPreviewMore', {
              count: assignedProducts.length - visibleProducts.length
            })}
          </Text>
        )}
      </Space>
    );
  };

  const columns: TableColumnsType<ProductCategory> = [
    {
      title: t('products:productCategory.name'),
      key: 'name',
      render: (_value, record) => (
        <Tag color={record.color || 'default'} style={{ fontSize: 14, padding: '2px 10px' }}>
          {record.name}
        </Tag>
      )
    },
    {
      title: t('products:productCategory.description'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string | null) => description || <Text type="secondary">—</Text>
    },
    {
      title: t('products:productCategory.dimensionDisplayProfile'),
      dataIndex: 'dimensionDisplayProfile',
      key: 'dimensionDisplayProfile',
      width: 260,
      render: (profile: string | null) => (
        <Tag>{t(`products:productCategory.dimensionProfiles.${profile || 'GENERIC'}`)}</Tag>
      )
    },
    {
      title: t('products:productCategory.productCount'),
      key: 'productCount',
      width: 160,
      align: 'center',
      render: (_value, record) => <Tag>{record.productCount ?? 0}</Tag>
    },
    {
      title: t('products:productCategory.assignedProducts'),
      key: 'assignedProducts',
      render: (_value, record) => renderProductPreview(record.id)
    }
  ];

  if (canEdit) {
    columns.push({
      title: '',
      key: 'actions',
      width: 154,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('products:productCategory.assignProducts')}>
            <Button
              size="small"
              icon={<AppstoreAddOutlined />}
              onClick={() => openAssignments(record)}
            />
          </Tooltip>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      )
    });
  }

  return (
    <div>
      <div className="app-page__card-intro">
        <Text type="secondary">{t('products:productCategory.tabHint')}</Text>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('products:productCategory.add')}
          </Button>
        )}
      </div>

      <Table<ProductCategory>
        columns={columns}
        dataSource={categories}
        rowKey="id"
        loading={loading || productsLoading}
        pagination={false}
        locale={{ emptyText: <Empty description={t('products:productCategory.empty')} /> }}
      />

      <Modal
        title={editing ? t('products:productCategory.edit') : t('products:productCategory.create')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={saving}
        maskClosable={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} preserve={false}>
          <Form.Item
            name="name"
            label={t('products:productCategory.name')}
            rules={[{ required: true, message: t('products:productCategory.nameRequired') }]}
          >
            <Input
              placeholder={t('products:productCategory.namePlaceholder')}
              maxLength={120}
            />
          </Form.Item>

          <Form.Item name="description" label={t('products:productCategory.description')}>
            <TextArea
              rows={3}
              maxLength={500}
              placeholder={t('products:productCategory.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item name="color" label={t('products:productCategory.color')}>
            <Select allowClear placeholder={t('products:productCategory.colorPlaceholder')}>
              {COLOR_PRESETS.map((color) => (
                <Select.Option key={color} value={color}>
                  <Space>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: color
                      }}
                    />
                    {color}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dimensionDisplayProfile"
            label={t('products:productCategory.dimensionDisplayProfile')}
            extra={t('products:productCategory.dimensionDisplayProfileHint')}
            initialValue="GENERIC"
          >
            <Select>
              {DIMENSION_DISPLAY_PROFILES.map((profile) => (
                <Select.Option key={profile} value={profile}>
                  {t(`products:productCategory.dimensionProfiles.${profile}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={assigningCategory
          ? t('products:productCategory.assignTitle', { name: assigningCategory.name })
          : t('products:productCategory.assignProducts')}
        open={assignmentModalOpen}
        onCancel={closeAssignmentModal}
        onOk={handleAssignmentSubmit}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingAssignments}
        okButtonProps={{ disabled: !assignmentReady || assignmentLoading }}
        maskClosable={false}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">{t('products:productCategory.assignHelp')}</Text>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('products:productCategory.assignPlaceholder')}
            value={selectedProductIds}
            onChange={setSelectedProductIds}
            open={assignmentSelectOpen}
            onOpenChange={setAssignmentSelectOpen}
            loading={assignmentLoading}
            disabled={!assignmentReady || assignmentLoading}
            style={{ width: '100%' }}
            maxTagCount="responsive"
            popupRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Button
                  type="primary"
                  block
                  icon={<CheckOutlined />}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setAssignmentSelectOpen(false)}
                >
                  {t('products:productCategory.selectionDone', {
                    count: selectedProductIds.length,
                  })}
                </Button>
              </>
            )}
          >
            {assignmentProductOptions.map((product) => (
              <Select.Option
                key={product.id}
                value={product.id}
                label={buildProductAssignmentSearchLabel(product)}
              >
                <Space direction="vertical" size={0}>
                  <Text>
                    {[product.name, formatCatalogProductDimensions(product)]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {[
                      product.ean,
                      product.productCategoryName
                        ? t('products:productCategory.currentlyInCategory', { name: product.productCategoryName })
                        : t('products:productCategory.none')
                    ].filter(Boolean).join(' · ')}
                  </Text>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Modal>
    </div>
  );
};

export default ProductCategoriesTab;
